import dayjs from "dayjs";

import {
  buildArtigoPet,
  buildPetRef,
  normalizeName,
  resolveMessage,
} from "../functions";

export const CRM_PET_RULES = {
  ABSENT: "ABSENT",
  NO_PRODUCT: "NO_PRODUCT",
  NO_ADDITIONAL: "NO_ADDITIONAL",
  POTENTIAL_SUBSCRIBER: "POTENTIAL_SUBSCRIBER",
};

export const CRM_PET_RULE_OPTIONS = [
  { value: CRM_PET_RULES.ABSENT, label: "Ausentes" },
  { value: CRM_PET_RULES.NO_PRODUCT, label: "Sem produtos" },
  { value: CRM_PET_RULES.NO_ADDITIONAL, label: "Sem adicionais" },
  { value: CRM_PET_RULES.POTENTIAL_SUBSCRIBER, label: "Possíveis assinantes" },
];

export const CRM_PET_DEFAULT_MESSAGES = {
  [CRM_PET_RULES.ABSENT]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\nJá faz {dias_ausente} dias que não vemos {nome_pet} por aqui. Estamos com saudades! 🐾🧡",
    consolidatedFragment: "• Estamos com saudades de {nome_pet}; já faz {dias_ausente} dias desde a última visita.",
  },
  [CRM_PET_RULES.NO_PRODUCT]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\nQue tal renovar os produtos de {nome_pet}? Estamos por aqui para ajudar a escolher tudo o que essa fofura precisa. 🐾🧡",
    consolidatedFragment: "• Podemos ajudar a renovar os produtos de {nome_pet}.",
  },
  [CRM_PET_RULES.NO_ADDITIONAL]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\nQue tal complementar os cuidados de {nome_pet}? Temos opções para deixar essa fofura ainda mais bem cuidada. 🐾🧡",
    consolidatedFragment: "• Temos opções para complementar os cuidados de {nome_pet}.",
  },
  [CRM_PET_RULES.POTENTIAL_SUBSCRIBER]: {
    individualTemplate: "Oi, {nome_cliente}! 😊\n\n{nome_pet} pode aproveitar os benefícios da assinatura Petland. Quer conhecer as opções? 🐾🧡",
    consolidatedFragment: "• {nome_pet} pode aproveitar os benefícios da assinatura Petland.",
  },
};

const RULE_FALLBACK_LABELS = {
  [CRM_PET_RULES.ABSENT]: "Ausente",
  [CRM_PET_RULES.NO_PRODUCT]: "Sem produto",
  [CRM_PET_RULES.NO_ADDITIONAL]: "Sem adicional",
  [CRM_PET_RULES.POTENTIAL_SUBSCRIBER]: "Potencial assinante",
};

export function getRuleLabel(ruleCode) {
  return RULE_FALLBACK_LABELS[ruleCode] || ruleCode;
}

function asString(value) {
  return value == null ? "" : String(value);
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

export function getMembershipKey(membership) {
  return `${membership.ruleCode}|${membership.subtypeCode}`;
}

export function getConfigLabel(config, ruleCode, subtypeCode) {
  const rule = (config?.rules || []).find((item) => item.ruleCode === ruleCode);
  const subtype = (rule?.subtypes || []).find((item) => item.subtypeCode === subtypeCode);
  return subtype?.label || RULE_FALLBACK_LABELS[ruleCode] || subtypeCode || ruleCode;
}

export function formatEvidence(membership = {}) {
  const evidence = membership.evidence || {};
  const windowDays = evidence.windowDays;
  const lastVisit = evidence.lastValidVisitAt;
  const visitText = lastVisit && dayjs(lastVisit).isValid()
    ? `Última visita em ${dayjs(lastVisit).format("DD/MM/YYYY")}`
    : null;

  if (membership.ruleCode === CRM_PET_RULES.ABSENT) {
    const days = lastVisit && dayjs(lastVisit).isValid()
      ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
      : null;
    return [days != null ? `${days} dias sem visita` : `Sem visita há ${windowDays || 30} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.NO_PRODUCT) {
    return [`Sem compra correspondente em ${windowDays || 90} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.NO_ADDITIONAL) {
    return [`Sem adicional correspondente em ${windowDays || 90} dias`, visitText].filter(Boolean).join(" · ");
  }
  if (membership.ruleCode === CRM_PET_RULES.POTENTIAL_SUBSCRIBER) {
    const planCount = evidence.compatiblePlanCodes?.length || 0;
    return planCount ? `${planCount} plano(s) compatível(is)` : "Pet elegível para assinatura";
  }
  return "Oportunidade ativa";
}

export function formatCrmPetConfig(payload = {}) {
  return {
    ...payload,
    faixas: Array.isArray(payload.faixas) ? payload.faixas : [],
    rules: (payload.rules || []).map((rule) => ({
      ...rule,
      active: rule.active === true,
      windowDays: Number(rule.windowDays || 0),
      subtypes: (rule.subtypes || []).map((subtype) => ({
        ...subtype,
        active: true,
        individualTemplate: rule.ruleCode !== CRM_PET_RULES.ABSENT && subtype.individualTemplate?.trim()
          ? subtype.individualTemplate
          : rule.ruleCode === CRM_PET_RULES.ABSENT
            ? subtype.individualTemplate || ""
            : CRM_PET_DEFAULT_MESSAGES[rule.ruleCode]?.individualTemplate || "",
        consolidatedFragment: rule.ruleCode !== CRM_PET_RULES.ABSENT && subtype.consolidatedFragment?.trim()
          ? subtype.consolidatedFragment
          : rule.ruleCode === CRM_PET_RULES.ABSENT
            ? subtype.consolidatedFragment || ""
            : CRM_PET_DEFAULT_MESSAGES[rule.ruleCode]?.consolidatedFragment || "",
        sourceRefs: {
          ...subtype.sourceRefs,
          productSubcategoryIds: subtype.sourceRefs?.productSubcategoryIds || [],
          serviceCategoryIds: subtype.sourceRefs?.serviceCategoryIds || [],
          serviceIds: subtype.sourceRefs?.serviceIds || [],
        },
      })),
    })),
  };
}

export function hasCompleteCrmPetMessages(config = {}) {
  return (config.rules || []).every((rule) => (
    rule.ruleCode === CRM_PET_RULES.ABSENT || !rule.subtypes?.length || rule.subtypes.every((subtype) => (
      Boolean(subtype.individualTemplate?.trim() && subtype.consolidatedFragment?.trim())
    ))
  ));
}

export function prepareCrmPetConfigPayload(config = {}) {
  return {
    faixas: config.faixas || [],
    rules: (config.rules || []).map((rule) => ({
      ...rule,
      subtypes: (rule.subtypes || []).map((subtype) => ({
        ...subtype,
        active: true,
      })),
    })),
  };
}

export function formatCustomerContactSummary(memberships = []) {
  const contactedMemberships = memberships.filter((membership) => membership.contactCount > 0);
  if (!contactedMemberships.length) {
    return { status: "PENDING", lastContactAt: null };
  }

  const lastContactAt = contactedMemberships.reduce((latest, membership) => {
    const current = membership.lastContactAt;
    if (!current || !dayjs(current).isValid()) return latest;
    if (!latest || dayjs(current).isAfter(dayjs(latest))) return current;
    return latest;
  }, null);

  return { status: "CONTACTED", lastContactAt };
}

export function formatOpportunitiesResponse(payload = {}) {
  const results = Array.isArray(payload.results) ? payload.results : [];
  const contractReady = results.every((opportunity) => opportunity?.pet);
  const customers = results.filter((opportunity) => opportunity?.pet).map((opportunity) => {
    const memberships = (opportunity.memberships || []).map((membership) => ({
      ...membership,
      membershipId: asString(membership.membershipId || membership._id),
      petIds: (membership.petIds?.length
        ? membership.petIds
        : opportunity.pet?.petId ? [opportunity.pet.petId] : []).map(asString),
      contactCount: Number(membership.contactCount || 0),
      ruleLabel: getRuleLabel(membership.ruleCode),
      label: membership.label || RULE_FALLBACK_LABELS[membership.ruleCode] || membership.subtypeCode,
      evidenceText: formatEvidence(membership),
    }));
    const rawPet = opportunity.pet || {};
    const pet = {
      ...rawPet,
      petId: asString(rawPet.petId),
      blocked: rawPet.blocked === true || rawPet.bloqueado === true,
      blockedAt: rawPet.blockedAt || rawPet.dataBloqueio || null,
    };
    const contactSummary = opportunity.contactSummary || {};

    return {
      ...opportunity,
      rowKey: opportunity.rowKey || `${opportunity.customerId || "customer"}:${pet.petId || "pet"}`,
      customerKey: opportunity.rowKey || `${opportunity.customerId || "customer"}:${pet.petId || "pet"}`,
      pet,
      pets: pet.petId ? [pet] : [],
      memberships,
      subscription: {
        status: opportunity.subscription?.status || null,
        frequencyPerMonth: opportunity.subscription?.frequencyPerMonth ?? null,
        lastSubscriptionAt: opportunity.subscription?.lastSubscriptionAt || null,
      },
      activity: {
        absenceDays: opportunity.activity?.absenceDays ?? null,
        lastAppointmentAt: opportunity.activity?.lastAppointmentAt || null,
        nextAppointmentAt: opportunity.activity?.nextAppointmentAt || null,
      },
      categoryActivity: opportunity.categoryActivity || {},
      contactSummary: {
        status: contactSummary.status === "CONTACTED" ? "CONTACTED" : "PENDING",
        lastContactAt: contactSummary.lastContactAt || null,
        contactCount: Number(contactSummary.contactCount || 0),
      },
    };
  });
  const pagination = payload.pagination || {};
  const summary = payload.summary || {};
  const normalizeFacets = (items) => (items || [])
    .map((item, index) => ({
      code: asString(item.code),
      label: item.label || item.code,
      order: Number.isFinite(Number(item.order)) ? Number(item.order) : index,
    }))
    .filter((item) => item.code)
    .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label));

  return {
    contractReady,
    customers,
    pagination: {
      page: Math.max(0, Number(pagination.currentPage || 1) - 1),
      perPage: Number(pagination.perPage || 10),
      totalDocuments: Number(pagination.total || 0),
      totalPages: Number(pagination.totalPages || 1),
    },
    summary: {
      totalAll: Number(summary.totalPets || 0),
      realizados: Number(summary.contactedPets || 0),
      pendentes: Number(summary.pendingPets || 0),
      bloqueados: Number(summary.blockedPets || 0),
    },
    facets: {
      productCategories: normalizeFacets(payload.facets?.productCategories),
      serviceCategories: normalizeFacets(payload.facets?.serviceCategories),
    },
  };
}

export function formatContactHistory(payload, config) {
  const items = Array.isArray(payload) ? payload : payload?.results || [];
  return items.map((item) => {
    const selections = item.selections?.length
      ? item.selections
      : (item.rules || []).map((ruleCode, index) => ({
        ruleCode,
        subtypeCode: item.subtypes?.[index] || "GENERAL",
      }));
    const pets = item.pets?.length
      ? item.pets
      : item.petId ? [{ petId: item.petId, petName: item.petName }] : [];
    return {
      ...item,
      contactedAt: item.contactedAt || item.dataContato || item.createdAt,
      responsibleName: item.responsibleName || item.responsavelNome || "-",
      message: item.message || item.observacao || "",
      pets,
      selections: selections.map((selection) => ({
        ...selection,
        ruleLabel: getRuleLabel(selection.ruleCode),
        label: getConfigLabel(config, selection.ruleCode, selection.subtypeCode),
      })),
      legacy: item.legacy === true,
    };
  });
}

function replaceVariables(template, customer, pets, membership) {
  const selectedPets = pets || [];
  const petNames = selectedPets.map((pet) => normalizeName(pet.petName) || pet.petName).filter(Boolean);
  const petReference = buildPetRef(selectedPets);
  const lastVisit = membership?.evidence?.lastValidVisitAt;
  const absenceDays = lastVisit && dayjs(lastVisit).isValid()
    ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
    : membership?.evidence?.windowDays || "";
  const values = {
    nome_cliente: normalizeName(customer.customerName) || customer.customerName || "cliente",
    nome_pet: petReference,
    nome_pets: petNames.join(" e ") || petReference,
    artigo_pet: buildArtigoPet(selectedPets),
    dias_ausente: absenceDays,
    nome_loja: "",
    telefone_loja: "",
  };
  return Object.entries(values).reduce(
    (message, [key, value]) => message.replace(new RegExp(`\\{${key}\\}`, "g"), asString(value)),
    template || "",
  );
}

function getRuleMessageConfig(config, ruleCode) {
  const rule = (config?.rules || []).find((item) => item.ruleCode === ruleCode);
  return (rule?.subtypes || []).find((subtype) => subtype.subtypeCode === "GENERAL")
    || rule?.subtypes?.[0]
    || null;
}

function getRuleMessageTemplate(config, membership, field) {
  const configuredTemplate = getRuleMessageConfig(config, membership.ruleCode)?.[field];
  if (configuredTemplate?.trim()) return configuredTemplate;
  return CRM_PET_DEFAULT_MESSAGES[membership.ruleCode]?.[field] || "";
}

function buildAbsenceMessage(customer, pets, membership, config) {
  const lastVisit = membership.evidence?.lastValidVisitAt;
  const absenceDays = lastVisit && dayjs(lastVisit).isValid()
    ? Math.max(0, dayjs().startOf("day").diff(dayjs(lastVisit).startOf("day"), "day"))
    : membership.evidence?.windowDays || 30;

  return resolveMessage(
    config?.faixas || [],
    absenceDays,
    customer.customerName,
    buildPetRef(pets),
    "",
    buildArtigoPet(pets),
  );
}

export function buildContactMessage({ customer, memberships, pets, config }) {
  if (!memberships?.length || !pets?.length) return "";
  if (memberships.length === 1) {
    const membership = memberships[0];
    if (membership.ruleCode === CRM_PET_RULES.ABSENT) {
      return buildAbsenceMessage(customer, pets, membership, config);
    }

    const individualTemplate = getRuleMessageTemplate(config, membership, "individualTemplate");
    if (individualTemplate) {
      return replaceVariables(individualTemplate, customer, pets, membership);
    }

    return replaceVariables(
      `Oi, {nome_cliente}! 😊\n\nIdentificamos uma oportunidade para {nome_pets}: ${membership.label}. Posso ajudar? 🐾🧡`,
      customer,
      pets,
      membership,
    );
  }

  const absenceMembership = memberships.find((membership) => (
    membership.ruleCode === CRM_PET_RULES.ABSENT
  ));
  const consolidatedMemberships = memberships.filter((membership) => (
    membership.ruleCode !== CRM_PET_RULES.ABSENT
  ));
  const fragments = unique(consolidatedMemberships.map((membership) => replaceVariables(
    getRuleMessageTemplate(config, membership, "consolidatedFragment") || `• ${membership.label}`,
    customer,
    pets,
    membership,
  )));

  if (absenceMembership) {
    const absenceMessage = buildAbsenceMessage(customer, pets, absenceMembership, config);
    return fragments.length ? `${absenceMessage}\n\n${fragments.join("\n")}` : absenceMessage;
  }

  const greeting = replaceVariables("Oi, {nome_cliente}! 😊", customer, pets);
  return `${greeting}\n\n${fragments.join("\n")}\n\nPosso ajudar com essas oportunidades para ${buildPetRef(pets)}? 🐾🧡`;
}

export function isMembershipCovered(membership, selectedPetIds) {
  return (membership.petIds || []).some((petId) => selectedPetIds.includes(petId));
}
